import { useMemo, useState, useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import useAvailabilityStore from '@/stores/availability.store'
import { useOfflineSync } from '@/hooks/useOfflineSync'
import { useWebSocket } from '@/hooks/useWebSocket'
import { cn } from '@/lib/cn'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { BedCounter } from '@/components/availability/BedCounter'
import { SpecialistToggle } from '@/components/availability/SpecialistToggle'
import { BloodStockGrid } from '@/components/availability/BloodStockGrid'
import { MedicationGrid } from '@/components/availability/MedicationGrid'
import { AmbulanceCounter } from '@/components/availability/AmbulanceCounter'
import { CompletenessBar } from '@/components/availability/CompletenessBar'
import { FreshnessIndicator } from '@/components/availability/FreshnessIndicator'
import { HospitalSelector } from '@/components/availability/HospitalSelector'
import {
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  BeakerIcon,
  CubeIcon,
  TruckIcon,
  WifiIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

export default function AvailabilityPage() {
  const store = useAvailabilityStore()
  const { isOnline, pendingCount, syncNow, submitOrQueue } = useOfflineSync()
  const { connectionStatus, lastMessage } = useWebSocket()

  // Track which bed service row to flash-highlight on real-time update
  const [highlightedService, setHighlightedService] = useState<string | null>(null)
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (lastMessage) {
      setHighlightedService(lastMessage.service)
      if (highlightTimerRef.current !== null) {
        clearTimeout(highlightTimerRef.current)
      }
      highlightTimerRef.current = setTimeout(() => {
        setHighlightedService(null)
        highlightTimerRef.current = null
      }, 1500)
    }
    return () => {
      if (highlightTimerRef.current !== null) {
        clearTimeout(highlightTimerRef.current)
      }
    }
  }, [lastMessage])

  const completeness = useMemo(() => {
    const bedsFilled = store.beds.some((b) => b.available > 0 || b.occupied > 0 || b.reserved > 0)
    const specialistsFilled = store.specialists.some((s) => s.isOnCall)
    const bloodFilled = store.bloodStocks.some((b) => b.quantity > 0)
    const medsFilled = store.medications.some((m) => m.quantity > 0)

    const sections = [
      { name: 'Lits', filled: bedsFilled },
      { name: 'Spécialistes', filled: specialistsFilled },
      { name: 'Sang', filled: bloodFilled },
      { name: 'Médicaments', filled: medsFilled },
    ]
    const filled = sections.filter((s) => s.filled).length
    return { percentage: Math.round((filled / sections.length) * 100), sections }
  }, [store.beds, store.specialists, store.bloodStocks, store.medications])

  const handleSubmit = async () => {
    const payload = {
      structureId: store.structureId || 'HPD',
      beds: store.beds.map((b) => ({
        service: b.service,
        available: b.available,
        occupied: b.occupied,
        reserved: b.reserved,
      })),
      specialists: store.specialists
        .filter((s) => s.isOnCall)
        .map((s) => ({ specialty: s.specialty, name: s.name })),
      bloodStocks: store.bloodStocks.map((b) => ({ group: b.group, quantity: b.quantity })),
      medications: store.medications.map((m) => ({ name: m.name, quantity: m.quantity })),
      ambulances: store.ambulances,
    }

    const sentOnline = await submitOrQueue(payload)

    if (sentOnline) {
      store.markSaved()
      store.setLastSyncTime(new Date().toISOString())
      toast.success('Disponibilités envoyées avec succès')
    } else {
      store.markSaved()
      toast('Hors ligne — déclaration sauvegardée, sera envoyée automatiquement', {
        icon: '\u{1F4E6}',
        duration: 5000,
      })
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-8 space-y-5">
      {/* Offline banner */}
      {!isOnline && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-warning-50 border border-warning-500 text-warning-700">
          <WifiIcon className="w-5 h-5 shrink-0" />
          <p className="text-sm font-medium">
            Vous êtes hors ligne. Les déclarations seront envoyées automatiquement au retour de la connexion.
          </p>
        </div>
      )}

      {/* Pending sync banner */}
      {pendingCount > 0 && isOnline && (
        <div className="flex items-center justify-between gap-2 px-4 py-3 rounded-lg bg-primary-50 border border-primary-200 text-primary-700">
          <div className="flex items-center gap-2">
            <ArrowPathIcon className="w-5 h-5 shrink-0" />
            <p className="text-sm font-medium">
              {pendingCount === 1
                ? '1 déclaration en attente de synchronisation'
                : `${pendingCount} déclarations en attente de synchronisation`}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={syncNow}>
            Synchroniser
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Déclaration des disponibilités</h1>
          <HospitalSelector />
        </div>
        <div className="flex items-center gap-3">
          {pendingCount > 0 && (
            <Badge variant="warning" size="sm">
              {pendingCount} en attente
            </Badge>
          )}
          <FreshnessIndicator lastUpdated={store.lastUpdated} />
          {/* WebSocket connection status indicator */}
          <div className="flex items-center gap-1.5" title={
            connectionStatus === 'connected'
              ? 'Temps réel connecté'
              : connectionStatus === 'reconnecting'
                ? 'Reconnexion en cours...'
                : 'Temps réel déconnecté'
          }>
            <div className={cn(
              'w-2 h-2 rounded-full',
              connectionStatus === 'connected' && 'bg-success-500',
              connectionStatus === 'reconnecting' && 'bg-warning-500 animate-pulse',
              connectionStatus === 'disconnected' && 'bg-error-500',
            )} />
            <span className="text-xs text-neutral-400">WS</span>
          </div>
        </div>
      </div>

      {/* Completeness */}
      <Card variant="bordered" padding="md">
        <CompletenessBar percentage={completeness.percentage} sections={completeness.sections} />
      </Card>

      {/* Beds */}
      <Card variant="bordered" padding="none">
        <div className="px-5 py-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <ClipboardDocumentCheckIcon className="w-5 h-5 text-primary-600" />
            <h3 className="text-base font-semibold text-neutral-900">Lits disponibles</h3>
          </div>
        </div>
        <div>
          {store.beds.map((bed) => (
            <div
              key={bed.service}
              className={cn(
                'transition-colors duration-700',
                highlightedService === bed.service && 'bg-primary-50'
              )}
            >
              <BedCounter
                serviceLabel={bed.serviceLabel}
                available={bed.available}
                occupied={bed.occupied}
                reserved={bed.reserved}
                total={bed.total}
                onUpdate={(field, value) => store.updateBed(bed.service, field, value)}
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Specialists */}
      <Card variant="bordered" padding="none">
        <div className="px-5 py-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <UserGroupIcon className="w-5 h-5 text-primary-600" />
            <h3 className="text-base font-semibold text-neutral-900">Spécialistes de garde</h3>
          </div>
        </div>
        <div>
          {store.specialists.map((sp) => (
            <SpecialistToggle
              key={sp.specialty}
              specialtyLabel={sp.specialtyLabel}
              name={sp.name}
              isOnCall={sp.isOnCall}
              onToggle={() => store.toggleSpecialist(sp.specialty)}
              onNameChange={(name) => store.updateSpecialistName(sp.specialty, name)}
            />
          ))}
        </div>
      </Card>

      {/* Blood stocks */}
      <Card variant="bordered">
        <Card.Header>
          <div className="flex items-center gap-2">
            <BeakerIcon className="w-5 h-5 text-error-500" />
            <Card.Title>Stocks sang</Card.Title>
          </div>
        </Card.Header>
        <Card.Content>
          <BloodStockGrid stocks={store.bloodStocks} onUpdate={store.updateBloodStock} />
        </Card.Content>
      </Card>

      {/* Medications */}
      <Card variant="bordered">
        <Card.Header>
          <div className="flex items-center gap-2">
            <CubeIcon className="w-5 h-5 text-warning-600" />
            <Card.Title>Médicaments critiques</Card.Title>
          </div>
        </Card.Header>
        <Card.Content>
          <MedicationGrid medications={store.medications} onUpdate={store.updateMedication} />
        </Card.Content>
      </Card>

      {/* Ambulances */}
      <Card variant="bordered">
        <Card.Header>
          <div className="flex items-center gap-2">
            <TruckIcon className="w-5 h-5 text-neutral-600" />
            <Card.Title>Ambulances disponibles</Card.Title>
          </div>
        </Card.Header>
        <Card.Content>
          <AmbulanceCounter
            smur={store.ambulances.smur}
            transport={store.ambulances.transport}
            onUpdate={store.updateAmbulances}
          />
        </Card.Content>
      </Card>

      {/* Submit */}
      <div className="bg-white py-4 rounded-lg border border-neutral-200 px-4">
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={handleSubmit}
          disabled={!store.isDirty}
        >
          {isOnline ? 'Envoyer les disponibilités' : 'Sauvegarder hors ligne'}
        </Button>
        {store.isDirty && (
          <p className="text-xs text-warning-600 text-center mt-2">
            Modifications non envoyées
          </p>
        )}
      </div>
    </div>
  )
}
